import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import ProfileFields from './ProfileFields';
import CommunityFields from './CommunityFields';
import { api, emptyProfile, profileForForm, emptyCommunity, communityForForm, communityLines, ago } from './api';

/** Accounts: the list, add existing / create new / generate with AI, pause, edit, remove. attrs: state, onPostAs(userId) */
export default class AccountsTab extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.mode = null; // 'existing' | 'new' | 'ai'
    this.form = {
      username: '',
      profile: emptyProfile(),
      community: emptyCommunity()
    };
    this.saving = false;
    this.editing = null; // userId
    this.editProfile = null;
    this.editCommunity = null;
    this.deleting = null; // { userId, preview, confirm, busy }
    this.ai = {
      count: 3,
      guidance: '',
      loading: false,
      proposals: null
    };
  }
  view() {
    const s = this.attrs.state;
    if (!s.accounts) return m(LoadingIndicator, null);
    const active = s.accounts.filter(a => a.status === 'active').length;
    return m("div", {
      className: "VEC-accounts"
    }, m("div", {
      className: "VEC-summary"
    }, m("div", null, m("strong", null, s.accounts.length), m("span", null, "accounts")), m("div", null, m("strong", null, active), m("span", null, "active")), m("div", null, m("strong", null, s.accounts.length - active), m("span", null, "paused")), m("div", {
      className: "VEC-summary-actions"
    }, m(Button, {
      className: 'Button' + (this.mode === 'existing' ? ' Button--primary' : ''),
      icon: "fas fa-user-plus",
      onclick: () => this.open('existing')
    }, "Add existing"), m(Button, {
      className: 'Button' + (this.mode === 'new' ? ' Button--primary' : ''),
      icon: "fas fa-user-edit",
      onclick: () => this.open('new')
    }, "Create new"), m(Button, {
      className: 'Button' + (this.mode === 'ai' ? ' Button--primary' : ''),
      icon: "fas fa-magic",
      onclick: () => this.open('ai')
    }, "Generate with AI"))), this.mode === 'existing' || this.mode === 'new' ? this.addForm() : null, this.mode === 'ai' ? this.aiPanel() : null, s.accounts.length ? m("ul", {
      className: "VEC-list"
    }, s.accounts.map(a => this.row(a))) : m("p", {
      className: "VEC-empty"
    }, "No managed accounts yet. Add your existing accounts or create new ones above."));
  }
  open(mode) {
    this.mode = this.mode === mode ? null : mode;
    this.form = {
      username: '',
      profile: emptyProfile(),
      community: emptyCommunity()
    };
  }
  addForm() {
    const existing = this.mode === 'existing';
    return m("form", {
      className: "VEC-card VEC-add",
      onsubmit: e => {
        e.preventDefault();
        this.add();
      }
    }, m("h3", null, existing ? 'Bring in an existing account' : 'Create a new account'), m("p", {
      className: "VEC-help"
    }, existing ? 'The account keeps its posts and history. It joins the Voucha Team group (its badge shows on every post) and can no longer be signed into directly: you post as it from here. Removing it later gives its own password back. Leave the My Community fields blank to keep the answers it already has.' : 'Created with a random password nobody knows and an internal address that gets no email. It joins the Voucha Team group.'), m("label", {
      className: "VEC-field"
    }, m("span", null, "Username"), m("input", {
      className: "FormControl",
      value: this.form.username,
      required: true,
      oninput: e => this.form.username = e.target.value,
      placeholder: existing ? 'Their current username' : '3 to 30 letters, numbers, - or _'
    })), m(ProfileFields, {
      profile: this.form.profile
    }), m(CommunityFields, {
      community: this.form.community,
      options: this.attrs.state.options
    }), m("div", {
      className: "VEC-actions"
    }, m(Button, {
      type: "submit",
      className: "Button Button--primary",
      loading: this.saving
    }, existing ? 'Add account' : 'Create account'), m(Button, {
      className: "Button",
      onclick: () => this.mode = null
    }, "Cancel")));
  }
  add() {
    this.saving = true;
    api('POST', '/accounts', {
      mode: this.mode,
      username: this.form.username,
      profile: this.form.profile,
      community: this.form.community
    }).then(() => {
      app.alerts.show({
        type: 'success'
      }, (this.mode === 'existing' ? 'Added ' : 'Created ') + this.form.username + '.');
      this.mode = null;
      return this.attrs.state.load();
    }).catch(() => {}).finally(() => {
      this.saving = false;
      m.redraw();
    });
  }
  aiPanel() {
    const s = this.attrs.state;
    if (!s.aiReady) {
      return m("div", {
        className: "VEC-card"
      }, m("p", {
        className: "VEC-help"
      }, "Add the OpenAI key in Admin > Extensions > Voucha Engagement to generate accounts."));
    }
    const ai = this.ai;
    return m("div", {
      className: "VEC-card VEC-ai"
    }, m("h3", null, "Generate account proposals"), m("p", {
      className: "VEC-help"
    }, "The AI drafts fictional profiles. Nothing is created until you tick the ones you want and press Create. Edit anything first."), m("div", {
      className: "VEC-ai-row"
    }, m("label", {
      className: "VEC-field is-narrow"
    }, m("span", null, "How many"), m("input", {
      className: "FormControl",
      type: "number",
      min: "1",
      max: "10",
      value: ai.count,
      oninput: e => ai.count = e.target.value
    })), m("label", {
      className: "VEC-field is-wide"
    }, m("span", null, "Guidance"), m("input", {
      className: "FormControl",
      value: ai.guidance,
      placeholder: "e.g. two in Canada, one in Kano, interested in tech, farming and property",
      oninput: e => ai.guidance = e.target.value
    })), m(Button, {
      className: "Button Button--primary",
      loading: ai.loading,
      onclick: () => this.propose()
    }, "Draft profiles")), ai.proposals ? ai.proposals.length ? m("div", null, ai.proposals.map(p => m("div", {
      className: 'VEC-proposal' + (p.pick ? ' is-picked' : '')
    }, m("label", {
      className: "VEC-pick"
    }, m("input", {
      type: "checkbox",
      checked: p.pick,
      onchange: e => p.pick = e.target.checked
    }), " Create"), m("label", {
      className: "VEC-field"
    }, m("span", null, "Username"), m("input", {
      className: "FormControl",
      value: p.username,
      oninput: e => p.username = e.target.value
    })), m(ProfileFields, {
      profile: p.profile
    }), m(CommunityFields, {
      community: p.community,
      options: this.attrs.state.options
    }))), m("div", {
      className: "VEC-actions"
    }, m(Button, {
      className: "Button Button--primary",
      loading: this.saving,
      disabled: !ai.proposals.some(p => p.pick),
      onclick: () => this.createPicked()
    }, "Create ", ai.proposals.filter(p => p.pick).length, " selected"))) : m("p", {
      className: "VEC-help"
    }, "No usable proposals came back. Try again or adjust the guidance.") : null);
  }
  propose() {
    this.ai.loading = true;
    api('POST', '/accounts/propose', {
      count: this.ai.count,
      guidance: this.ai.guidance
    }).then(r => {
      this.ai.proposals = (r.proposals || []).map(p => ({
        pick: false,
        username: p.username,
        profile: profileForForm(p),
        community: communityForForm(p.community)
      }));
    }).catch(() => {}).finally(() => {
      this.ai.loading = false;
      m.redraw();
    });
  }
  async createPicked() {
    this.saving = true;
    m.redraw();
    const picked = this.ai.proposals.filter(p => p.pick);
    let made = 0;
    for (const p of picked) {
      try {
        await api('POST', '/accounts', {
          mode: 'new',
          fromAi: true,
          username: p.username,
          profile: p.profile,
          community: p.community
        });
        made++;
        this.ai.proposals = this.ai.proposals.filter(x => x !== p);
      } catch (e) {
        // the alert says why (e.g. username taken); the proposal stays so it can be fixed
      }
    }
    this.saving = false;
    if (made) app.alerts.show({
      type: 'success'
    }, 'Created ' + made + (made === 1 ? ' account.' : ' accounts.'));
    await this.attrs.state.load();
    m.redraw();
  }
  row(a) {
    const p = a.profile || {};
    const editing = this.editing === a.userId;
    return m("li", {
      className: 'VEC-account is-' + a.status,
      key: a.userId
    }, m("div", {
      className: "VEC-account-main"
    }, a.avatarUrl ? m("img", {
      className: "VEC-avatar",
      src: a.avatarUrl,
      alt: ""
    }) : m("span", {
      className: "VEC-avatar"
    }, (a.displayName || a.username).charAt(0).toUpperCase()), m("div", {
      className: "VEC-account-text"
    }, m("div", {
      className: "VEC-account-name"
    }, m("a", {
      href: app.route('user', {
        username: a.username
      }),
      target: "_blank",
      rel: "noopener"
    }, a.displayName), m("span", {
      className: "VEC-muted"
    }, "@", a.username), m("span", {
      className: 'VEC-pill is-' + a.status
    }, a.status === 'active' ? 'Active' : 'Paused'), a.origin === 'existing' ? m("span", {
      className: "VEC-pill"
    }, "Existing account") : null), m("div", {
      className: "VEC-muted"
    }, communityLines(a.community).join(' · ') || 'No My Community details yet'), m("div", {
      className: "VEC-muted"
    }, a.discussionCount, " discussions \xB7 ", a.commentCount, " posts \xB7 last posted ", ago(a.lastPostedAt))), m("div", {
      className: "VEC-account-actions"
    }, m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-pen",
      disabled: a.status !== 'active',
      onclick: () => this.attrs.onPostAs(a.userId)
    }, "Post as"), m(Button, {
      className: "Button",
      onclick: () => this.toggleEdit(a)
    }, editing ? 'Close' : 'Edit'), m(Button, {
      className: "Button",
      onclick: () => this.setStatus(a, a.status === 'active' ? 'paused' : 'active')
    }, a.status === 'active' ? 'Pause' : 'Resume'), m(Button, {
      className: "Button",
      onclick: () => this.remove(a)
    }, "Remove"), m(Button, {
      className: "Button Button--danger",
      icon: "fas fa-trash",
      onclick: () => this.startDelete(a)
    }, "Delete"))), this.deleting && this.deleting.userId === a.userId ? this.deletePanel(a) : null, editing ? m("form", {
      className: "VEC-edit",
      onsubmit: e => {
        e.preventDefault();
        this.saveProfile(a);
      }
    }, m(ProfileFields, {
      profile: this.editProfile
    }), m(CommunityFields, {
      community: this.editCommunity,
      options: this.attrs.state.options
    }), m("div", {
      className: "VEC-actions"
    }, m(Button, {
      type: "submit",
      className: "Button Button--primary",
      loading: this.saving
    }, "Save profile"))) : null);
  }
  toggleEdit(a) {
    this.editing = this.editing === a.userId ? null : a.userId;
    this.editProfile = profileForForm(a.profile);
    this.editCommunity = communityForForm(a.community);
  }
  saveProfile(a) {
    this.saving = true;
    api('POST', '/accounts/' + a.userId, {
      profile: this.editProfile,
      community: this.editCommunity
    }).then(() => {
      this.editing = null;
      return this.attrs.state.load();
    }).catch(() => {}).finally(() => {
      this.saving = false;
      m.redraw();
    });
  }
  setStatus(a, status) {
    api('POST', '/accounts/' + a.userId, {
      status
    }).then(() => this.attrs.state.load()).catch(() => {}).finally(() => m.redraw());
  }
  startDelete(a) {
    if (this.deleting && this.deleting.userId === a.userId) {
      this.deleting = null;
      return;
    }
    this.deleting = {
      userId: a.userId,
      preview: null,
      confirm: '',
      busy: false
    };
    api('GET', '/accounts/' + a.userId + '/delete').then(r => {
      if (this.deleting && this.deleting.userId === a.userId) this.deleting.preview = r;
    }).catch(() => this.deleting = null).finally(() => m.redraw());
  }
  deletePanel(a) {
    const d = this.deleting;
    const p = d.preview;
    const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);
    return m("div", {
      className: "VEC-delete"
    }, m("h3", null, m("i", {
      className: "fas fa-exclamation-triangle"
    }), " Delete ", a.username, " and everything it posted"), !p ? m("p", {
      className: "VEC-help"
    }, "Counting what would be deleted\u2026") : m("div", null, m("ul", null, m("li", null, plural(p.discussions, 'discussion it started', 'discussions it started'), ", with every reply in them", p.othersRepliesInTheirDiscussions ? m("strong", null, ", including ", plural(p.othersRepliesInTheirDiscussions, 'reply', 'replies'), " by ", plural(p.otherMembersAffected, 'other member', 'other members')) : null), m("li", null, plural(p.repliesElsewhere, 'post', 'posts'), " it made in other discussions"), m("li", null, "The account itself, its likes, votes and notifications")), m("p", {
      className: "VEC-help"
    }, "This can't be undone. To keep the posts but stop using the account, use Remove instead."), m("label", {
      className: "VEC-field is-narrow"
    }, m("span", null, "Type ", a.username, " to confirm"), m("input", {
      className: "FormControl",
      value: d.confirm,
      oninput: e => d.confirm = e.target.value,
      autocomplete: "off"
    })), m("div", {
      className: "VEC-actions"
    }, m(Button, {
      className: "Button Button--danger",
      loading: d.busy,
      disabled: d.confirm.trim().toLowerCase() !== a.username.toLowerCase(),
      onclick: () => this.confirmDelete(a)
    }, "Delete permanently"), m(Button, {
      className: "Button",
      onclick: () => this.deleting = null
    }, "Cancel"))));
  }
  confirmDelete(a) {
    const d = this.deleting;
    d.busy = true;
    api('POST', '/accounts/' + a.userId + '/delete', {
      confirm: d.confirm
    }).then(r => {
      app.alerts.show({
        type: 'success'
      }, 'Deleted ' + a.username + ': ' + r.discussions + ' discussions and ' + r.repliesElsewhere + ' other posts.');
      this.deleting = null;
      return this.attrs.state.load();
    }).catch(() => {
      if (this.deleting) this.deleting.busy = false;
    }).finally(() => m.redraw());
  }
  remove(a) {
    const msg = 'Take ' + a.username + ' out of the Engagement Centre?\n\nThe account and its posts stay. It leaves the Voucha Team group' + (a.origin === 'existing' ? ' and its own password works again.' : '. It was created here, so nobody knows its password: it will just sit unused.');
    if (!window.confirm(msg)) return;
    api('DELETE', '/accounts/' + a.userId).then(() => this.attrs.state.load()).catch(() => {}).finally(() => m.redraw());
  }
}